import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { OrionIconButton } from "@orion/OrionComponents/OrionIconButton";

import { type UploadItem } from "../uploadManager";

import { CloseIcon, ImageOutlinedIcon } from "@/icons";

type Props = {
  item: UploadItem;
  onCancel: (id: string) => void;
};

const formatStatus = (item: UploadItem): { text: string; color: string } => {
  switch (item.status) {
    case "queued":
      return { text: "Waiting…", color: "text.secondary" };
    case "uploading":
      return { text: `${item.progress}% uploaded`, color: "text.primary" };
    case "done":
      return { text: "Done", color: "success.main" };
    case "failed":
      return {
        text: item.errorMessage ? `Failed — ${item.errorMessage}` : "Failed",
        color: "error.main",
      };
  }
};

export function UploadProgressRow({ item, onCancel }: Props) {
  const status = formatStatus(item);
  return (
    <Box sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <ImageOutlinedIcon
          sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.fileName}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: status.color }}>
          {status.text}
        </Typography>
        <OrionIconButton
          aria-label={`Cancel upload of ${item.fileName}`}
          onClick={() => onCancel(item.id)}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </OrionIconButton>
      </Stack>
      {item.status === "uploading" && (
        <LinearProgress
          variant="determinate"
          value={item.progress}
          sx={{ mt: 2, height: 2 }}
        />
      )}
    </Box>
  );
}
